import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { touchSession, whoami, logout } from "../lib/auth";
import { getBroadcastChannel } from "../lib/realtime";
import { nextCheckDelay } from "../lib/mandatoryReload";
import type { SessionUser } from "../types/auth";

interface Props {
  allowedRoles?: string[];
  children: ((user: SessionUser) => ReactNode) | ReactNode;
}

/**
 * Wraps a route. If the visitor has no valid session, redirects to /login.
 * If the visitor IS authenticated, renders `children` (passing the SessionUser
 * if children is a function).
 * Optionally supports role-based gating via `allowedRoles`.
 */
export default function AuthGate({ allowedRoles, children }: Props) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "anon" }
    | { kind: "ok"; user: SessionUser }
  >({ kind: "loading" });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const user = await whoami();
        if (!mounted) return;
        if (!user) {
          setState({ kind: "anon" });
          return;
        }
        setState({ kind: "ok", user });
        // Fire-and-forget: extend session expiry on boot
        void touchSession();
      } catch (err) {
        if (!mounted) return;
        console.error("AuthGate lookup failed:", err);
        // Fallback to anon to force sign in if no valid cached profile exists
        setState({ kind: "anon" });
      }
    })();

    // Listen for tablet session revocation broadcast. The channel is public, so the event is only a
    // hint: whoami asks the server, and only a real 401/403 signs this tablet out. Events are
    // coalesced into one server check per 10 s so a flood of forged events cannot hammer
    // bar-whoami (security plan phase 4, verifier note).
    const ch = getBroadcastChannel();
    let lastCheckAt = 0;
    let checkTimer: ReturnType<typeof setTimeout> | null = null;
    const checkSession = () => {
      checkTimer = null;
      if (!mounted) return;
      lastCheckAt = Date.now();
      whoami()
        .then((verified) => {
          if (!verified && mounted) {
            logout();
            setState({ kind: "anon" });
          }
        })
        .catch(() => {
          /* offline with no cached profile: keep the session; the next real check decides */
        });
    };
    const handleBroadcast = () => {
      if (!mounted || checkTimer) return;
      checkTimer = setTimeout(checkSession, nextCheckDelay(lastCheckAt, Date.now()));
    };
    ch.on("broadcast", { event: "tablet_session_revoked" }, handleBroadcast);

    return () => {
      mounted = false;
      if (checkTimer) clearTimeout(checkTimer);
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          color: "var(--muted)",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  if (state.kind === "anon") {
    return <Navigate to="/login" replace />;
  }

  // Enforce role gating if allowedRoles are supplied
  if (allowedRoles && allowedRoles.length > 0) {
    const hasRole = state.user.bar_roles?.some((r) => allowedRoles.includes(r));
    if (!hasRole) {
      console.warn(`AuthGate: Access denied for ${state.user.display_label}. Required roles: ${allowedRoles.join(", ")}`);
      return <Navigate to="/" replace />;
    }
  }

  return (
    <>{typeof children === "function" ? children(state.user) : children}</>
  );
}
